<script setup lang="ts">
import EstateRibbons from '../shared/EstateRibbons.vue'
import EstateDetailRepresentative from './EstateDetailRepresentative.vue'
import EstateDetailMain from './EstateDetailMain.vue'
import EstateDetailClosest from './EstateDetailClosest.vue'
import EstateDetailHeader from './EstateDetailHeader.vue'
import Container from '~/components/layout/Container.vue'
import ImageGallery from '~/components/global/ImageGallery.vue'
import CustomOfferModal from '~/components/modals/CustomOfferModal.vue'

import type { EstateDetailInterface } from '~/types/api/EstateDetail'
import type { EstateType } from '~/types/EstateType'
import ArrangeVisitModal from '~/components/modals/ArrangeVisitModal.vue'
import getEstateTrackingData from '~/utils/tracking/getEstateTrackingData'
import trackGtmEvent from '~/utils/tracking/tackGtmEvent'
import useCustomOfferModalStore from '~/stores/modals/useCustomOfferModal'

const props = defineProps<{
  estateType: EstateType
  estateData: EstateDetailInterface
}>()

const customOfferModalStore = useCustomOfferModalStore()

const galleryItems = computed(() => {
  if (!props.estateData.images.length) return ['/images/no-image.svg']

  return props.estateData.images
})

onMounted(() => {
  trackGtmEvent('videnie_detailu', getEstateTrackingData())
})
</script>

<template>
  <section class="c-estate-detail">
    <Container class="c-estate-detail__container">
      <div class="c-estate-detail__header">
        <EstateDetailHeader
          :estate-data="estateData"
          :estate-type="estateType"
        />
      </div>

      <div class="c-estate-detail__gallery">
        <EstateRibbons
          :estate-data="estateData"
          size="large"
          class="sc-ribbons"
        />

        <ImageGallery
          gallery-id="estate-detail-main"
          :images="galleryItems"
          :virtual-tour-link="estateData.image_space"
          :preload-images="true"
          :has-no-image="estateData.images.length < 1"
        />
      </div>

      <div class="c-estate-detail__main">
        <EstateDetailMain :estate-data="estateData" />
      </div>

      <div class="c-estate-detail__representative">
        <EstateDetailRepresentative :estate-data="estateData" />
      </div>

      <div class="c-estate-detail__similar">
        <EstateDetailClosest
          :estate-data="estateData"
          :estate-type="estateType"
        />
      </div>
    </Container>
  </section>

  <CustomOfferModal :estate-data="estateData" />
  <ArrangeVisitModal :estate-data="estateData" />
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;

.c-estate-detail {
  padding-bottom: 150px;

  &__container {
    display: grid;
    grid-template:
      'header header' auto
      'gallery gallery' auto
      'main representative' auto
      'similar similar' auto
      / 2.25fr 1fr;
    gap: 0 20px;

    @include breakpoint-down('lg') {
      grid-template:
        'header' auto
        'gallery' auto
        'main' auto
        'representative' auto
        'similar' auto
        / 1fr;
    }
  }

  &__header {
    grid-area: header;
    margin-bottom: 32px;
  }

  &__gallery {
    grid-area: gallery;
    margin-bottom: 60px;
    position: relative;

    .sc-ribbons {
      grid-area: gallery;
      position: absolute;
      z-index: 2;
      top: 20px;
      left: 20px;

      @include breakpoint-down('md') {
        top: 10px;
        left: 10px;
      }
    }

    @include breakpoint-down('xl') {
      margin-bottom: 40px;
    }
  }

  &__main {
    grid-area: main;
    max-width: 760px;
    min-width: 0;
    margin-bottom: 56px;

    @include breakpoint-down('xl') {
      max-width: 100%;
      margin-bottom: 48px;
    }

    @include breakpoint-down('lg') {
      margin-bottom: 0;
    }
  }

  &__representative {
    grid-area: representative;
    min-width: 0;
    margin-bottom: 56px;

    @include breakpoint-down('lg') {
      margin-bottom: 0;
      position: sticky;
      bottom: calc(var(--container-padding) + 7px);
      max-width: calc(100% - 90px);
    }
    @media (max-width: 480px) {
      bottom: var(--container-padding);
      max-width: calc(100% - 70px);
    }
  }

  &__similar {
    grid-area: similar;
    border-top: 1px solid var(--color-border);
    min-width: 0;
  }
}
</style>
